import { singularize } from 'lib/helpers/utility';

import { getOrdinal, isEmptyOrBlank } from 'components/shared/ExpandableToolbar/lib/helpers';

const parseArrayIndexKey = (key) => {
  // Match pattern like "procedures.0.units" or "procedures.0.primary.0.ins_adj.0.amount"
  // Stops after the first array index (e.g., "procedures.0")
  const match = key.match(/^([^.]+)\.(\d+)\.(.+)$/);

  if (match) {
    return {
      basePath: match[1],
      index: parseInt(match[2], 10),
      fieldName: match[3],
      isArrayIndexed: true
    };
  }
  return { isArrayIndexed: false, key };
};

const expandActivity = (activity, previouslyCreatedNestedFields) => {
  const { action, currentValues = {}, previousValues = {} } = activity;

  // Get all keys from both currentValues and previousValues
  const allKeys = new Set([...Object.keys(currentValues), ...Object.keys(previousValues)]);

  // Check if any keys contain array indices
  const hasArrayIndexedKeys = Array.from(allKeys).some((key) => {
    const parsed = parseArrayIndexKey(key);
    return parsed.isArrayIndexed;
  });

  // If no array-indexed keys, return the activity as-is
  if (!hasArrayIndexedKeys) {
    return [activity];
  }

  // Group keys by base path and index
  const groupedByIndex = {};

  allKeys.forEach((key) => {
    const parsed = parseArrayIndexKey(key);

    if (!parsed.isArrayIndexed) return;

    const { basePath, index, fieldName } = parsed;
    const groupKey = `${basePath}.${index}`;

    if (!groupedByIndex[groupKey]) {
      groupedByIndex[groupKey] = {
        basePath,
        index,
        fields: []
      };
    }

    if (!groupedByIndex[groupKey].fields.includes(fieldName)) {
      groupedByIndex[groupKey].fields.push(fieldName);
    }
  });

  // If we have array-indexed keys, create separate entries for each index
  const expandedActivities = [];

  // First, handle non-nested fields (fields that don't have array indices)
  // Group all non-nested fields into a single activity
  const nonNestedCurrentValues = {};
  const nonNestedPreviousValues = {};
  let hasNonNestedFields = false;

  allKeys.forEach((key) => {
    const parsed = parseArrayIndexKey(key);
    if (!parsed.isArrayIndexed) {
      // This is a non-nested field, collect it
      const fieldName = parsed.key;
      hasNonNestedFields = true;

      if (fieldName in currentValues) {
        nonNestedCurrentValues[fieldName] = currentValues[fieldName];
      }

      if (fieldName in previousValues) {
        nonNestedPreviousValues[fieldName] = previousValues[fieldName];
      }
    }
  });

  // Create a single activity for all non-nested fields if any exist
  if (hasNonNestedFields) {
    const isNonNestedCreate =
      action === 'create' || (action === 'update' && isEmptyOrBlank(nonNestedPreviousValues));

    const nonNestedActivity = {
      ...activity,
      isNestedCreate: isNonNestedCreate,
      currentValues: nonNestedCurrentValues,
      previousValues: nonNestedPreviousValues
    };

    expandedActivities.push(nonNestedActivity);
  }

  // Then, handle nested fields (fields with array indices)
  // Sort by basePath first, then by index to ensure consistent ordering
  Object.values(groupedByIndex)
    .sort((a, b) => {
      if (a.basePath !== b.basePath) {
        return a.basePath.localeCompare(b.basePath);
      }
      return a.index - b.index;
    })
    .forEach((group) => {
      const { basePath, index, fields } = group;
      const newCurrentValues = {};
      const newPreviousValues = {};

      // Extract values for this index group
      fields.forEach((fieldName) => {
        const fullKey = `${basePath}.${index}.${fieldName}`;

        if (fullKey in currentValues) {
          newCurrentValues[fieldName] = currentValues[fullKey];
        }

        if (fullKey in previousValues) {
          newPreviousValues[fieldName] = previousValues[fullKey];
        }
      });

      // Create new activity object with all original properties except currentValues and previousValues
      // Generate singular form of basePath using singularize utility
      const singularBasePath = singularize(basePath);
      const nestedField = `${getOrdinal(index)} ${singularBasePath}`;
      const isNestedCreate =
        action === 'create' || (action === 'update' && isEmptyOrBlank(newPreviousValues));

      // Track if this nested field was previously created
      const wasPreviouslyCreated = previouslyCreatedNestedFields.has(nestedField);

      // If this is a create, add it to the tracking set for future activities
      if (isNestedCreate) {
        previouslyCreatedNestedFields.add(nestedField);
      }

      const newActivity = {
        ...activity,
        isNestedCreate: isNestedCreate && !wasPreviouslyCreated,
        prefix: nestedField, // temp used in formatChangedValues
        currentValues: newCurrentValues,
        previousValues: newPreviousValues
      };

      expandedActivities.push(newActivity);
    });

  return expandedActivities;
};

export const expandActivityLogByIndex = (activities = []) => {
  if (!Array.isArray(activities) || activities.length === 0) {
    return activities;
  }

  // Track which nested fields have been created previously
  const previouslyCreatedNestedFields = new Set();

  // Process all activities and flatten the results
  return activities.flatMap((activity) => expandActivity(activity, previouslyCreatedNestedFields));
};
